import { IconTransformOutline } from '@icons/IconTransformOutline'
import { Button } from '@/components/shadcn/button'
import { Textarea } from '@/components/shadcn/textarea'
import { RestSchemas } from '@/contexts/QueryProvider'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import clsx from 'clsx'
import { Suspense, useState } from 'react'
import { ActivityLogV2Props } from '../ActivityLogV2'
import { TimelineFileUploader } from '../TimeLineFileUploader'
import { NextStepHeading } from './NextStepHeading'
import { useGetFileRequirements } from './useGetFileRequirements'
import { usePostTransitionUpdate } from './usePostTransitionUpdate'
import { useTimeLineFileUploader } from '../TimeLineFileUploader/useTimelineFileUploader'
import { IconV2UploadOutline } from '@icons/IconV2UploadOutline'

type LogItem = RestSchemas['Messages.Templates.TTemplateActivity']

export function StartAction({ entityId, templateId, lastActivity }: ActivityLogV2Props & { lastActivity: LogItem }) {
  return (
    <>
      <NextStepHeading>Upload a new file</NextStepHeading>
      <Suspense fallback={<div>Loading...</div>}>
        <StartActionContents entityId={entityId} templateId={templateId} lastActivity={lastActivity} />
      </Suspense>
    </>
  )
}

function StartActionContents({ entityId, templateId, lastActivity }: ActivityLogV2Props & { lastActivity: LogItem }) {
  const { appId, projectId } = useAppContext()
  const {
    data: { fileSizeLimit, extensionsAllowed, mimeTypesAllowed },
  } = useGetFileRequirements({ entityId, templateId })
  const { mutate: transitionUpdate, isPending } = usePostTransitionUpdate({ entityId, templateId })
  const { allow_comment } = lastActivity.file_template
  const [comment, setComment] = useState('')
  const accept = mimeTypesAllowed.reduce(
    (acc, mimeType) => {
      acc[mimeType] = extensionsAllowed.map((extension) => extension.toLowerCase())
      return acc
    },
    {} as Record<string, string[]>
  )
  const { files, dragging, selectFiles, dropZoneConfig } = useTimeLineFileUploader(accept, fileSizeLimit)

  const handleUpload = () => {
    if (files.length === 0) return
    if (!appId || !projectId) return
    const formData = new FormData()
    formData.append('application_id', appId.toString())
    formData.append('project_id', projectId.toString())
    formData.append('entity_id', entityId.toString())
    formData.append('template_id', templateId.toString())
    formData.append('transition', 'Upload')
    formData.append('file', files[0], files[0].name)
    if (comment) formData.append('notes', comment)
    transitionUpdate({ formData })
  }

  const handleCancel = () => {
    selectFiles(null)
    setComment('')
  }

  if (isPending)
    return (
      <div className="flex w-full items-center justify-center gap-x-2 py-6">
        <IconTransformOutline className="size-6 animate-spin text-primary" />
        <p className="text-sm">Uploading...</p>
      </div>
    )

  return (
    <>
      <TimelineFileUploader
        files={files}
        selectFiles={selectFiles}
        dragging={dragging}
        dropZoneConfig={dropZoneConfig}
      />
      <div className={clsx('flex flex-col gap-y-2', { hidden: files.length === 0 })}>
        {allow_comment && (
          <Textarea placeholder="Add a comment" value={comment} onChange={(e) => setComment(e.target.value)} />
        )}
        <div className="flex justify-end gap-x-2">
          <Button size="sm" className="h-7" onClick={handleCancel} variant="outline">
            Cancel
          </Button>
          <Button size="sm" className="h-7" onClick={handleUpload}>
            <IconV2UploadOutline className="mr-1 size-3" />
            Upload
          </Button>
        </div>
      </div>
    </>
  )
}
